import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { ReducerStatus, RootState } from '../types';
import { CartState } from './types';
import {
    CartPaymentStatus,
    ActivityProduct,
    AddonProduct,
    AdyenPaymentMethod,
    Agency,
    Bonus,
    BookingOrigin,
    BookingSalesChannel,
    CartAffiliate,
    Customer,
    DownPayment,
    FerryProduct,
    FlightPax,
    FlightProduct,
    GiftCard,
    HotelProduct,
    PartPaymentMethodType,
    PartPaymentOption,
    PaymentMethod,
    Product,
    ProductType,
    Room,
    Seating,
    TransferProduct,
    TransferMainPassenger,
    AddonType,
    BonusNightProduct,
    SerializationType,
} from '@/api/Cart/cart.interfaces';
import { endpoints } from '@/api/endpoints';
import { Money } from '@libs/money/money.interfaces';
import { isFlightOnly, isBonusNightProduct } from '@/helpers/cart';
import { AnyProduct } from '@shared/interfaces/product';
import { trackClarity } from '@/tracking/clarity';
import { ClarityEvent } from '@/tracking/clarity/types';
import { seatingActions, seatingGetters, seatingMutations } from './seating';
import { isMainProduct, mapCartValues } from '@/api/Cart/cart.helpers';
import { getSelectedBaggages } from '@shared/helpers/baggage';
import { t } from '@shared/helpers/translation';
import { isEmpty } from 'lodash';
import { RedirectionType, redirectTo } from '@/helpers/url';
import { CartProductCombination } from '@shared/interfaces/productCombination';
import { getProductPricing } from '@/helpers/price';
import { getUpdatedAddons } from '@/helpers/termsAndPoliciesLinks';
import { getShieldText } from '@/helpers/transfer/transfer';

const initialState: CartState = {
    status: ReducerStatus.INITIAL,
    data: {
        ShoppingCartKey: '',
        BookingToken: '',
        FlowContainsPreCheckout: false,
        FlowContainsPostCheckout: false,
        Agency: null,
        Customer: {
            Email: '',
            PhoneNumber: '',
            ReceiveNewsletter: true,
            CountryCode: '',
            IsLoggedIn: false,
        },
        Products: [],
        ProductCombination: null,
        AvailableAddons: [],
        Payment: {
            PaymentId: '',
            PaymentDiscount: null,
            DiscountedPaymentMethods: [],
            PaymentMethods: [],
            SelectedPaymentOption: null,
            SelectedPartPaymentOption: 0,
            GiftCards: [],
            AmountAlreadyPaid: { Amount: 0, Currency: 'SEK' },
            CampaignDiscount: null,
            PaymentDueTime: null,
            PartPaymentOptions: [],
        },
        AvailableBonus: [],
        NextUrl: '',
        ErrorMessage: null,
        Affiliate: null,
        BookingSalesChannels: [],
        EstimatedMargin: null,
    },
    error: null,
};

const state: CartState = {
    ...initialState,
};

const getters: GetterTree<CartState, RootState> = {
    getShoppingCartKey(state): string | null {
        return state.data?.ShoppingCartKey || null;
    },

    getProductCombination(state): CartProductCombination | null {
        return state.data?.ProductCombination || null;
    },

    getAgency(state): Agency | null {
        return state.data?.Agency || null;
    },

    getCustomer(state): Customer {
        return state.data?.Customer ?? initialState.data.Customer;
    },

    getCampaignDiscount(state): Money | null {
        return state.data?.Payment?.CampaignDiscount || null;
    },

    getAmountAlreadyPaid(state): Money | null {
        return state.data?.Payment?.AmountAlreadyPaid || null;
    },

    getPaymentMethods(state) {
        return state.data?.Payment?.PaymentMethods || [];
    },

    getGiftCards(state): GiftCard[] {
        return state.data?.Payment?.GiftCards ?? [];
    },

    getProducts(state): AnyProduct[] {
        return state.data?.Products || [];
    },

    getProduct:
        (_, getter) =>
        (id: string): AnyProduct | undefined => {
            return (getter.getProducts as AnyProduct[]).find((x) => x.Id === id);
        },

    getMainProducts(_, getter): AnyProduct[] {
        const products = getter.getProducts as AnyProduct[];
        return [...products].filter((x) => isMainProduct(x.Type));
    },

    getTransferProducts(_, getter): AnyProduct[] {
        const products = getter.getProducts as AnyProduct[];
        return [...products].filter((x) => x.Type === ProductType.Transfer);
    },

    getSelectedAddons(_, getter): AddonProduct[] {
        return (getter.getProducts as AnyProduct[]).filter(
            ({ Type }) => Type === ProductType.Addon
        ) as AddonProduct[];
    },

    getTransferProductByTripId:
        (_, getter) =>
        (tripId: string): TransferProduct | undefined => {
            const transfers = getter.getTransferProducts as TransferProduct[];
            return transfers.find((t) => t.TripId === tripId);
        },

    getIsFlightOnly(state): boolean {
        return isFlightOnly(state.data);
    },

    getFlightProducts(_, getter): FlightProduct[] {
        return (getter.getProducts as AnyProduct[]).filter(
            ({ Type }) => Type === ProductType.Flight || Type === ProductType.Train
        ) as FlightProduct[];
    },

    hasFlightProducts(_, getter): boolean {
        return getter.getFlightProducts.length > 0;
    },

    getActivityProducts(_, getter): ActivityProduct[] {
        return (getter.getProducts as AnyProduct[]).filter(
            ({ Type }) => Type === ProductType.Activity
        ) as ActivityProduct[];
    },

    hasActivityProducts(_, getter): boolean {
        return getter.getActivityProducts.length > 0;
    },

    getHotelProducts(_, getter): HotelProduct[] {
        return (getter.getProducts as AnyProduct[]).filter(
            ({ Type }) => Type === ProductType.Hotel || Type === ProductType.Golf
        ) as HotelProduct[];
    },

    hasHotelProducts(_, getter): boolean {
        return getter.getHotelProducts.length > 0;
    },

    getFerryProducts(_, getter): FerryProduct[] {
        return (getter.getProducts as AnyProduct[]).filter(
            ({ Type }) => Type === ProductType.Ferry
        ) as FerryProduct[];
    },

    hasFerryProducts(_, getter): boolean {
        return getter.getFerryProducts.length > 0;
    },

    getBonusNightProducts(_, getter): BonusNightProduct[] {
        return (getter.getProducts as AnyProduct[]).filter(
            isBonusNightProduct
        ) as BonusNightProduct[];
    },

    hasBonusNightProducts(_, getter): boolean {
        return getter.getBonusNightProducts.length > 0;
    },

    getCurrency(_, getter) {
        const products = getter.getProducts;
        return products.length > 0 ? products[0].Price.Currency : 'SEK';
    },

    getSelectedPartPaymentOption(state): PartPaymentMethodType | null {
        return state.data?.Payment?.SelectedPartPaymentOption;
    },

    getSelectedPaymentOption(state): PaymentMethod | null {
        return state.data?.Payment?.SelectedPaymentOption;
    },

    getPartPaymentOptions(state): PartPaymentOption[] {
        return state.data?.Payment?.PartPaymentOptions ?? [];
    },

    getDiscountedPaymentMethods(state): string[] {
        return state.data?.Payment?.DiscountedPaymentMethods ?? [];
    },

    getServicePackageAddons(state): AddonProduct[] {
        return (state.data?.AvailableAddons ?? []).filter((addon) => {
            return addon.$type === SerializationType.ServicePackage;
        });
    },

    getAvailableAddons(state): AddonProduct[] {
        const addons = state.data?.AvailableAddons ?? [];
        if (isEmpty(addons)) {
            return [];
        }
        const updatedAddons = getUpdatedAddons(addons);
        const supportedAddons: AddonType[] = [
            'CancellationInsurance',
            'LuggageInsurance',
            'TravelInsurance',
        ];
        return updatedAddons.filter((addon) =>
            supportedAddons.includes(addon.$type as AddonType)
        );
    },

    getConfirmationSMSAddon(state): AddonProduct | undefined {
        const addons = state.data?.AvailableAddons ?? [];

        return addons.find(
            (addon) => addon.$type === SerializationType.FlightConfirmationSms
        );
    },

    getAvailableBonus(state): Bonus[] {
        return (state.data?.AvailableBonus || []).filter((x) => x.Value.Amount > 0);
    },

    getAffiliate(state): CartAffiliate | null {
        return state.data?.Affiliate || null;
    },

    getBookingToken(state): string | null {
        return state.data?.BookingToken ?? null;
    },

    getBookingSalesChannels(state): BookingSalesChannel[] {
        return state.data?.BookingSalesChannels ?? [];
    },

    getProductsTotalAmount(state, getter): Money {
        const products = (getter.getProducts ?? []) as AnyProduct[];

        const total = products.reduce(
            (productTotal: number, item: Product) =>
                productTotal + getProductPricing(item).price.Amount,
            0
        );

        return {
            Amount: total,
            Currency: getter.getCurrency,
        };
    },

    getAmountAlreadyPaidAmount(_, getter): Money {
        return {
            Amount: getter.getAmountAlreadyPaid?.Amount || 0,
            Currency: getter.getCurrency,
        };
    },

    getCampaignDiscountAmount(_, getter): Money {
        return {
            Amount: getter.getCampaignDiscount?.Amount || 0,
            Currency: getter.getCurrency,
        };
    },

    getAgencyCommisionAmount(_, getter): Money {
        return {
            Amount: getter.getAgency?.Commission?.Amount || 0,
            Currency: getter.getCurrency,
        };
    },

    getGiftCardsAmount(_, getter): Money {
        const cards = getter.getGiftCards as GiftCard[];

        const total = cards.reduce(
            (sum: number, card: GiftCard) => sum + (card.Value?.Amount ?? 0),
            0
        );

        return {
            Amount: total,
            Currency: getter.getCurrency,
        };
    },

    getEstimatedMargin(state): Money | null {
        return state.data?.EstimatedMargin ?? null;
    },

    getTotalToPay(state, getter): Money {
        if (!state.data) {
            return {
                Amount: 0,
                Currency: getter.getCurrency,
            };
        }

        const totalPriceBaggages = getter.getBaggagesTotalPrice
            ? getter.getBaggagesTotalPrice.Amount
            : 0;

        const totalPriceGordianSeating = getter.getGordianSeatingTotalPrice
            ? getter.getGordianSeatingTotalPrice.Amount
            : 0;

        const totalPriceSeating = getter.getSeatingTotalPrice
            ? getter.getSeatingTotalPrice.Amount
            : 0;
        const giftCards = getter.getGiftCardsAmount.Amount;
        const totalPriceProducts = getter.getProductsTotalAmount.Amount;
        const amountAlreadyPaid = getter.getAmountAlreadyPaidAmount.Amount;
        const campaignDiscount = getter.getCampaignDiscountAmount.Amount;
        const agencyCommission = getter.getAgencyCommisionAmount.Amount;
        const paymentDiscount = getter.getPaymentMethodDiscount.Amount;
        const paymentMethodDiscount =
            getter.paymentMethodDiscountApplies && paymentDiscount ? paymentDiscount : 0;

        const totalToPay =
            totalPriceProducts > 0
                ? totalPriceProducts -
                  amountAlreadyPaid -
                  campaignDiscount -
                  giftCards -
                  agencyCommission -
                  totalPriceSeating +
                  totalPriceGordianSeating +
                  totalPriceBaggages -
                  paymentMethodDiscount
                : 0;

        return {
            Amount: totalToPay > 0 ? totalToPay : 0,
            Currency: getter.getCurrency,
        };
    },

    getTotalDownPaymentToPay(_: any, getter: any): DownPayment {
        const options: PartPaymentOption[] = getter.getPartPaymentOptions;

        const downPaymentOption = options.find(
            (option) => option.Type === PartPaymentMethodType.DownPayment
        );

        const downPaymentAmount = downPaymentOption?.Amount.Amount || 0;
        const giftCardsAmount = getter.getGiftCardsAmount.Amount;
        const partPayment = Math.max(downPaymentAmount - giftCardsAmount, 0);

        const totalToPay = getter.getTotalToPay.Amount;
        const remainingPayment = Math.max(totalToPay - partPayment, 0);

        return {
            Date: downPaymentOption?.DueDateForNextPayment ?? null,
            Price: {
                Now: { Amount: partPayment, Currency: getter.getCurrency },
                Remaining: {
                    Amount: remainingPayment,
                    Currency: getter.getCurrency,
                },
            },
        };
    },

    getSeatingTotalPrice(state, getter): Money | null {
        let selected: Seating[] = [];
        const flightProducts = getter.getFlightProducts as FlightProduct[];

        for (const p of flightProducts) {
            selected = p.Seating ? [...selected, p.Seating] : [...selected];
        }

        if (selected.length === 0) {
            return null;
        }

        const currency = selected[0].Price.Currency;
        const totalAll: Money = selected.reduce(
            (sum, seat) => {
                sum.Amount += seat.Price.Amount;
                return sum;
            },
            { Amount: 0, Currency: currency }
        );

        return totalAll.Amount > 0 ? totalAll : null;
    },

    getBaggagesTotalPrice(state, getter): Money | null {
        let total: Money[] = [];
        const flightProducts = getter.getFlightProducts as FlightProduct[];

        for (const p of flightProducts) {
            const productTotal = p.Pax.reduce(
                (sum: Money, pax: FlightPax) => {
                    const selected = getSelectedBaggages(pax?.AdditionalBaggage);

                    if (!isEmpty(selected)) {
                        selected.forEach((s) => {
                            sum.Amount += s.Price?.Amount ?? 0;
                            sum.Currency = s.Price.Currency;
                        });
                    }

                    return sum;
                },
                { Amount: 0, Currency: p.Price.Currency }
            );

            total = [...total, productTotal];
        }

        if (total.length === 0) {
            return null;
        }

        const totalAllProducts = total.reduce(
            (sum, flightTotal) => {
                sum.Amount += flightTotal.Amount;
                sum.Currency = flightTotal.Currency;
                return sum;
            },
            {
                Amount: 0,
                Currency: total[0].Currency,
            }
        );

        return totalAllProducts.Amount > 0 ? totalAllProducts : null;
    },

    getTotalToPayExcludingPaymentMethodDiscount(_, getter): Money {
        const totalToPay = getter.getTotalToPay.Amount;

        const paymentOption = getter.getSelectedPaymentOption;
        const discount = getter.getPaymentMethodDiscount.Amount;

        if (discount > 0 && paymentOption === null) {
            return {
                Amount: totalToPay + discount,
                Currency: getter.getCurrency,
            };
        } else {
            return {
                Amount: totalToPay,
                Currency: getter.getCurrency,
            };
        }
    },

    getPaymentMethodDiscount(state, getter): Money {
        const discount = state.data?.Payment?.PaymentDiscount;

        return discount !== null && discount !== undefined
            ? discount
            : {
                  Amount: 0,
                  Currency: getter.getCurrency,
              };
    },

    getPaymentStatus(_, getter): CartPaymentStatus {
        const totalToPay = getter.getTotalToPay.Amount;
        const amountAlreadyPaid = getter.getAmountAlreadyPaidAmount.Amount;

        const unpaid = amountAlreadyPaid === 0;
        const fullyPaid = amountAlreadyPaid === totalToPay;

        if (unpaid) {
            return CartPaymentStatus.Unpaid;
        } else if (fullyPaid) {
            return CartPaymentStatus.Paid;
        } else {
            return CartPaymentStatus.PartiallyPaid;
        }
    },

    // TODO: Rewrite so we only get the selected one.....
    getPaymentMethodDiscountLabel(state): string | null {
        const discountedPaymentMethods = state.data?.Payment?.DiscountedPaymentMethods;

        if (!discountedPaymentMethods) {
            return null;
        }

        const paymentMethodsWithDiscount = discountedPaymentMethods.map((pm) => {
            const label = t({
                key: `payment.${pm.toLowerCase()}_payment_label`,
            });
            if (label && !label.startsWith('payment')) return label;
            else return pm;
        });

        return [...new Set(paymentMethodsWithDiscount)]
            .join(', ')
            .replace(/, ([^,]*)$/, ' & $1');
    },

    getIsLoggedIn(state): boolean {
        return state.data?.Customer?.IsLoggedIn ?? false;
    },

    paymentSelectionIsDone(_, getter): boolean {
        const paymentOption = getter.getSelectedPaymentOption;
        const partPaymentOption = getter.getSelectedPartPaymentOption;

        return paymentOption !== null || partPaymentOption !== PartPaymentMethodType.Unknown;
    },

    paymentMethodDiscountApplies(_, getter): boolean {
        const discountedPaymentMethods = getter.getDiscountedPaymentMethods;
        const partPaymentOptions = getter.getPartPaymentOptions;
        const partPaymentOption = getter.getSelectedPartPaymentOption;
        const paymentOption = getter.getSelectedPaymentOption as PaymentMethod | null;
        const paymentDiscount = getter.getPaymentMethodDiscount;

        const hasDiscount = discountedPaymentMethods.length > 0 && paymentDiscount !== null;

        const isAdyenCardPayment =
            paymentOption?.$type === 'AdyenPaymentMethod' && paymentOption.Code === 'card';

        const adyenCardPayment = isAdyenCardPayment
            ? (paymentOption as AdyenPaymentMethod)
            : null;

        const cardBrand = adyenCardPayment?.CardBrand;
        const hasCardBrand = cardBrand && cardBrand !== 'card';

        const hasValidPaymentMethod = hasCardBrand || (!adyenCardPayment && !!paymentOption);

        if (hasValidPaymentMethod && hasDiscount && partPaymentOption !== null) {
            const code = (cardBrand ?? paymentOption?.Code ?? '').toUpperCase();
            return discountedPaymentMethods.some(
                (method: string) => method.toUpperCase() === code
            );
        }

        const isUnknownPartPayment =
            partPaymentOptions.length > 0 &&
            partPaymentOption === PartPaymentMethodType.Unknown;

        if (hasDiscount && (isUnknownPartPayment || !hasValidPaymentMethod)) {
            return true;
        }

        return false;
    },

    totalFullOrDownPaymentPrice(_, getter): Money {
        const currency = getter.getCurrency;
        const selectedPartPaymentOption = getter.getSelectedPartPaymentOption;

        const selectedPaymentMethod = getter.getSelectedPaymentOption;

        switch (selectedPartPaymentOption) {
            case PartPaymentMethodType.FullPayment:
                if (selectedPaymentMethod === null && getter.getPaymentMethodDiscount) {
                    return {
                        Amount:
                            getter.getTotalToPay.Amount -
                            getter.getPaymentMethodDiscount.Amount,
                        Currency: currency,
                    };
                } else {
                    return getter.getTotalToPay;
                }

            case PartPaymentMethodType.DownPayment: {
                const partPaymentOptions = getter.getPartPaymentOptions;
                const indexOfDownPayment = partPaymentOptions
                    .map((partPaymentOption: PartPaymentOption) => partPaymentOption.Type)
                    .indexOf(PartPaymentMethodType.DownPayment);
                if (indexOfDownPayment === -1) {
                    return { Amount: 0, Currency: currency };
                }
                const remainingDownpayment =
                    state.data.Payment.PartPaymentOptions[indexOfDownPayment].Amount.Amount -
                    (getter.getGiftCardsAmount?.Amount ?? 0);
                const partPayment = remainingDownpayment > 0 ? remainingDownpayment : 0;
                return { Amount: partPayment, Currency: currency };
            }
            case PartPaymentMethodType.Unknown:
                if (selectedPaymentMethod === null && getter.getPaymentMethodDiscount) {
                    return {
                        Amount:
                            getter.getTotalToPay.Amount -
                            getter.getPaymentMethodDiscount.Amount,
                        Currency: currency,
                    };
                } else {
                    return getter.getTotalToPay;
                }
            default:
                return getter.getTotalToPay;
        }
    },

    dueDownPaymentPrice(_, getter): Money {
        return {
            Amount: getter.getTotalToPay.Amount - getter.totalFullOrDownPaymentPrice.Amount,
            Currency: getter.getCurrency,
        };
    },

    ...seatingGetters,
};

const mutations: MutationTree<CartState> = {
    updateProductsPax(state, payload) {
        state.data.Products = state.data.Products.map((product) => {
            const newProduct = payload.products.find((p: AnyProduct) => p.Id === product.Id);

            if (product.Type === ProductType.Flight || product.Type === ProductType.Ferry) {
                return newProduct ? { ...product, Pax: newProduct.Pax } : product;
            } else if (product.Type === ProductType.Hotel) {
                return newProduct ? { ...product, Rooms: newProduct.Rooms } : product;
            }
            return product;
        });
    },

    updateProducts(state, payload) {
        state.data.Products = state.data.Products.map((product) => {
            const newProduct = payload.products.find((p: AnyProduct) => p.Id === product.Id);
            return newProduct ?? product;
        });
    },

    clearCart(state) {
        Object.assign(state, initialState);
    },

    setCartState(state, payload) {
        Object.assign(state, payload);
    },

    updateHotelGuestsState(state, payload) {
        (state.data.Products[payload.hotelIndex] as HotelProduct).Rooms[
            payload.roomIndex
        ].Pax = payload.guestInformation;
    },
    updateFlightPassengerBaggageState(state, payload) {
        const index = state.data.Products.findIndex((x) => x.Id === payload.product.Id);

        if (index > -1) {
            (state.data.Products[index] as FlightProduct).Pax[
                payload.paxIndex
            ].AdditionalBaggage.Alternatives = payload.alternatives;
        }
    },
    updateFerryPassengerState(state, payload) {
        (state.data.Products[payload.ferryIndex] as FerryProduct).Pax[payload.paxIndex] =
            payload.passengerInformation;
    },
    updateFerryVehicleState(state, payload) {
        const ferry = state.data.Products[payload.ferryIndex] as FerryProduct;

        ferry.Legs[payload.legIndex].Segments[0].Vehicles![
            payload.vehicleIndex
        ].LicensePlate = payload.plate;
    },
    updateCustomerState(state, payload) {
        state.data.Customer = payload;
    },
    updateAgencyState(state, payload) {
        if (payload) {
            Object.assign(state.data.Agency || {}, payload);
        }
    },
    setCampaignDiscount(state, payload) {
        state.data.Payment.CampaignDiscount = payload;
    },

    setError(state, payload) {
        state.error = payload;
    },

    buyAddon(state, payload: AddonProduct) {
        state.data.Products.push(payload);
    },

    updateSeatingProduct(state, payload) {
        const seatingProducts = state.data.Products.filter((x) => x.Type === 'Flight').filter(
            (x) => (x as FlightProduct).Seating
        );

        const index = seatingProducts.findIndex((p) => {
            const item = p as FlightProduct;

            return item.Seating
                ? item.Seating.SeatMapIdentifier === payload.SeatMapIdentifier
                : -1;
        });

        const product = index !== -1 ? (seatingProducts[index] as FlightProduct) : null;
        if (product && product.Seating) {
            Object.assign(product.Seating, payload);
        }
    },
    updateSpecialRequest(state, payload) {
        const indexOfAddonProductToUpdate = state.data.Products.findIndex(
            (product) => product.Id === payload.Id
        );
        Object.assign(state.data.Products[indexOfAddonProductToUpdate], payload);
    },
    copyRoomPaxToRooms(state, currentRoom: Room) {
        const hotels = state.data.Products.filter(
            ({ Type }) => Type === ProductType.Hotel || Type === ProductType.Golf
        ) as HotelProduct[];

        const hotelRef = hotels.find((hotel) =>
            hotel.Rooms.map((r) => r.Id).includes(currentRoom.Id)
        );
        const roomIndex = hotelRef?.Rooms.findIndex((r) => r.Id === currentRoom.Id) ?? 0;

        hotels
            .filter((hotel) => hotel.Id !== hotelRef?.Id && hotel.Rooms.length >= roomIndex)
            .forEach((hotel) => {
                hotel.Rooms[roomIndex].Pax = { ...currentRoom.Pax };
            });
    },
    removeAddon(state, payload: AddonProduct) {
        state.data.Products = state.data.Products.filter(
            (product: Product) => product.Id !== payload.Id
        );
    },
    setPartPaymentOption(state, payload) {
        state.data.Payment.SelectedPartPaymentOption = payload;
    },
    setSelectedPaymentMethod(state, payload: PaymentMethod) {
        state.data.Payment.SelectedPaymentOption = payload;
    },
    setProductUpdating(state, payload: { updating: boolean; id: string }) {
        state.data.Products = [
            ...state.data.Products.map((product) => ({
                ...product,
                Updating: product.Id === payload.id ? payload.updating : product.Updating,
            })),
        ];
    },
    ...seatingMutations,
};

const actions: ActionTree<CartState, RootState> = {
    updateProductsPax({ state, commit }, payload) {
        commit('updateProductsPax', {
            ...state,
            products: payload,
        });
    },
    updateProducts({ state, commit }, payload) {
        commit('updateProducts', {
            ...state,
            products: payload,
        });
    },
    setCartLoading({ state, commit }) {
        commit('setCartState', {
            ...state,
            status: ReducerStatus.LOADING,
        });
    },
    setCartUpdating({ state, commit }) {
        commit('setCartState', {
            ...state,
            status: ReducerStatus.UPDATING,
        });
    },
    setCartSuccess({ state, commit }, payload) {
        commit('setCartState', {
            ...state,
            data: mapCartValues(payload),
            error: null,
            status: ReducerStatus.LOADED,
        });
    },
    setCartError({ state, commit }, payload) {
        trackClarity(ClarityEvent.CART_AND_OPTIONS_ERROR_MESSAGE);

        commit('setCartState', {
            ...state,
            data: null,
            error: payload,
            status: ReducerStatus.ERROR,
        });
    },

    resetCartUpdating({ state, commit }) {
        commit('setCartState', {
            ...state,
            status: ReducerStatus.LOADED,
        });
    },

    clearCart({ commit }) {
        commit('clearCart');
    },

    updateHotelGuestsState({ commit, state }, payload) {
        for (const hotel of payload) {
            let hotelIndex = -1;

            for (let i = 0; i < state.data.Products.length; i++) {
                const product = state.data.Products[i];

                if (product.Id === hotel.HotelId && product.Type.toString() === 'Hotel') {
                    // if we want to use the ProductType reference instead of hard-coding I think we need to change ProductType to an enum instead of a type
                    hotelIndex = i;
                }
            }

            const roomIndex = (state.data.Products[hotelIndex] as HotelProduct).Rooms.map(
                (room) => room.Id
            ).indexOf(hotel.RoomId);
            const guestInformation = {
                Title: hotel.Title,
                FirstName: hotel.FirstName,
                LastName: hotel.LastName,
                SpecialRequest: hotel.SpecialRequest,
            };
            commit('updateHotelGuestsState', {
                hotelIndex,
                roomIndex,
                guestInformation,
            });
        }
    },
    updateFerryPassengersState({ commit, state }, payload) {
        for (const ferry of payload) {
            const ferryIndex = state.data.Products.map(
                (product: Product) => product.Id
            ).indexOf(ferry.FerryId);

            commit('updateFerryPassengerState', {
                ferryIndex,
                paxIndex: ferry.PaxIndex,
                passengerInformation: { ...ferry },
            });
        }
    },

    updateFerryVehiclesState({ commit, state }, payload) {
        const ferryIndex = state.data.Products.map((product: Product) => product.Id).indexOf(
            payload.ferryId
        );

        commit('updateFerryVehicleState', {
            ferryIndex,
            ...payload,
        });
    },

    updateTransferMainPassenger(
        { commit, state },
        payload: { productId: string; mainPassenger: TransferMainPassenger }
    ) {
        const updatedProducts = state.data.Products.map((x) => {
            if (x.Id === payload.productId && x.Type === ProductType.Transfer) {
                return {
                    ...x,
                    MainPassenger: payload.mainPassenger,
                    ShieldText: getShieldText(payload.mainPassenger),
                };
            }

            return x;
        });
        commit('updateProducts', {
            ...state,
            products: updatedProducts,
        });
    },

    updateCustomerState({ commit }, payload) {
        commit('updateCustomerState', payload);
    },
    updateAgencyState({ commit }, payload) {
        commit('updateAgencyState', payload);
    },
    copyRoomPaxToRooms({ commit }, payload) {
        commit('copyRoomPaxToRooms', payload);
    },
    async setCampaignDiscount({ commit }, payload) {
        await commit('setCampaignDiscount', payload);
    },
    async deleteProduct({ commit, dispatch, state }, { product, connectedIds }) {
        commit('setProductUpdating', { updating: true, id: product.Id });

        const remove = [product.Id, ...connectedIds];

        const newCartState = {
            ...state.data,
            Products: state.data.Products.filter((p: Product) => !remove.includes(p.Id)),
        };

        try {
            const response = await endpoints.cart.UpdateCart(
                newCartState,
                BookingOrigin.Checkout,
                false
            );
            if (response.ErrorMessage && response.ErrorMessage !== '') {
                throw response.ErrorMessage;
            } else {
                dispatch('setCartSuccess', response);

                if (response.NextUrl && response.NextUrl !== '') {
                    redirectTo(response.NextUrl, RedirectionType.Replace);
                }
            }
        } finally {
            commit('setProductUpdating', { updating: false, id: product.Id });
        }
    },
    ...seatingActions,
};

export const cart: Module<CartState, RootState> = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
